import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { ChevronDown, ExternalLink, Briefcase } from "lucide-react";

/** ---------- Hook: InView (IntersectionObserver) ---------- */
function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setInView(true);
          // Trigger once only:
          obs.disconnect();
        }
      },
      { threshold: 0.2, ...options }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);

  return { ref, inView };
}

/** ---------- Hook: CountUp ---------- */
function useCountUp(target: number, start: boolean, durationMs = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let raf = 0;
    const startTime = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const next = Math.round(eased * target);
      setValue(next);

      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start, durationMs]);

  return value;
}

/** ---------- Component: Reveal wrapper (CSS-only animation) ---------- */
const Reveal: React.FC<{
  children: React.ReactNode;
  delayMs?: number;
  className?: string;
}> = ({ children, delayMs = 0, className = "" }) => {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delayMs}ms`,
      }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
};

const HamdanGroup: React.FC<{ content?: any }> = ({ content }) => {
  const { t, t_api } = useLanguage();
  const navigate = useNavigate();
  // Mini stats: trigger once when mini-stats enter viewport
  const { ref: statsRef, inView: statsInView } = useInView<HTMLDivElement>({
    threshold: 0.25,
  });

  const foundedVal = useCountUp(parseInt(content?.stats?.founded) || 1975, statsInView, 1000);
  const unitsVal = useCountUp(parseInt(content?.stats?.units) || 14, statsInView, 900);
  const divisionsVal = useCountUp(parseInt(content?.stats?.divisions) || 3, statsInView, 800);
  const locationsVal = useCountUp(parseInt(content?.stats?.locations) || 12, statsInView, 700);

  const statCards = useMemo(
    () => [
      { label: t_api(content?.stats?.stat_1_label || "Founded", content?.stats?.stat_1_label_ar), value: foundedVal.toString(), suffix: "" },
      { label: t_api(content?.stats?.stat_2_label || "Units", content?.stats?.stat_2_label_ar), value: unitsVal.toString(), suffix: "+" },
      { label: t_api(content?.stats?.stat_3_label || "Divisions", content?.stats?.stat_3_label_ar), value: divisionsVal.toString(), suffix: "" },
      { label: t_api(content?.stats?.stat_4_label || "Strategic Locations", content?.stats?.stat_4_label_ar), value: locationsVal.toString(), suffix: "" },
    ],
    [foundedVal, unitsVal, divisionsVal, locationsVal, content, t_api]
  );

  return (
    <div className="bg-white">
      {/* local keyframes (no extra CSS file needed) */}
      <style>{`
        @keyframes floatSoft {
          0%,100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        .float-soft { animation: floatSoft 4s ease-in-out infinite; }
      `}</style>

      {/* ================= HERO ================= */}
      <section className=" bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <Reveal>
            <p className="text-xs font-semibold tracking-widest text-red-500 uppercase">
              {t_api(content?.intro_heading || "About • Hamdan Group", content?.intro_heading_ar)}
            </p>
          </Reveal>

          <div className="mt-3 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <Reveal delayMs={80} className="max-w-3xl">
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">
                {t_api(content?.main_title || "Hamdan Trading Group", content?.main_title_ar)}
              </h1>
              <div 
                className="mt-4 text-gray-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t_api(content?.intro || "Hamdan Trading Group is part of one of the largest corporate conglomerates...", content?.intro_ar).replace(/\n/g, '<br/>') }}
              />
            </Reveal>

            {/* Mini stats (COUNT + REVEAL) */}
            <div ref={statsRef} className="flex flex-wrap gap-4">
  {statCards.map((s, idx) => (
    <Reveal key={s.label} delayMs={140 + idx * 80}>
      <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm hover:shadow-md transition-shadow">
        
        {/* Number bubble */}
        <div className="h-10 min-w-10 px-3 rounded-xl bg-[#F2BB13]/25 flex items-center justify-center font-extrabold text-[#C1121F] float-soft">
          {s.value}
          {s.suffix}
        </div>

        {/* Label */}
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-700 font-bold">
            {s.label}
          </p>
        </div>

      </div>
    </Reveal>
  ))}
</div>

          </div>
        </div>
      </section>

      {/* ================= MAIN ================= */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* ================= LEFT: TEXT CONTENT ================= */}
          <div className="lg:col-span-8 space-y-10">
            {/* Background */}
            <Reveal>
              <div className="rounded-3xl border border-gray-200 p-7 md:p-9 hover:shadow-sm transition-shadow">
                <h2 className="text-1xl md:text-2xl font-bold text-gray-900 mb-6">
                  {t_api(content?.backgroundTitle || "Background", content?.backgroundTitle_ar)}
                </h2>

                <div className="mt-4 space-y-4 text-gray-700 leading-relaxed text-justify text-[15px] sm:text-sm md:text-base">
                  <div dangerouslySetInnerHTML={{ __html: t_api(content?.backgroundDesc || "", content?.backgroundDesc_ar).replace(/\n/g, '<br/>') }} />
                </div>
              </div>
            </Reveal>

            {/* CSR */}
            <Reveal delayMs={80}>
              <div className="rounded-3xl border border-gray-200 p-7 md:p-9 bg-gray-50 hover:shadow-sm transition-shadow">
                <h2 className="text-1xl md:text-2xl font-bold text-gray-900 mb-6">
                  {t_api(content?.csr?.title || "Corporate Social Responsibility", content?.csr?.title_ar)}
                </h2>

                <div 
                    className="space-y-4 text-gray-700 leading-relaxed text-justify text-[15px] sm:text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: t_api(content?.csr?.desc || "", content?.csr?.desc_ar).replace(/\n/g, '<br/>') }}
                />

                {content?.csr?.building_title && (
                    <Reveal delayMs={120}>
                        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
                            <p className="text-sm font-bold text-gray-900">
                                {t_api(content.csr.building_title, content.csr.building_title_ar)}
                            </p>
                            <p className="mt-3 text-[15px] sm:text-sm md:text-base text-gray-700 leading-relaxed text-justify">
                                {t_api(content.csr.building_desc, content.csr.building_desc_ar)}
                            </p>
                        </div>
                    </Reveal>
                )}
              </div>
            </Reveal>

            {/* Awards */}
            <Reveal delayMs={120}>
              <div className="rounded-3xl border border-gray-200 p-7 md:p-9 hover:shadow-sm transition-shadow">
                <h2 className="text-1xl md:text-2xl font-bold text-gray-900 mb-6">
                  {t_api(content?.awards?.title || "Awards & Achievements", content?.awards?.title_ar)}
                </h2>

                <div className="space-y-6 text-gray-700 leading-relaxed text-justify text-[15px] sm:text-sm md:text-base">
                    {/* Main Description */}
                    <div dangerouslySetInnerHTML={{ __html: t_api(content?.awards?.desc || "", content?.awards?.desc_ar).replace(/\n/g, '<br/>') }} />
                    
                    {/* Award Points as simple paragraphs (without bars or titles) */}
                    {content?.awards?.points?.filter((p: any) => p.desc).map((award: any, idx: number) => (
                         <p key={idx}>
                            {t_api(award.desc, award.desc_ar)}
                         </p>
                    ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* ================= RIGHT: INFO PANEL ================= */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Image */}
              <Reveal>
                <div className="rounded-1xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white overflow-hidden">
                  <img
                    src={content?.image_1 || "/about-hamdan-trading-group.jpg"}
                    alt="Hamdan Trading Group"
                    className="max-w-full max-h-full object-contain p-1"
                  />
                </div>
              </Reveal>

              {/* Quick Facts */}
              <Reveal delayMs={90}>
                <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6 hover:shadow-sm transition-shadow">
                  <h3 className="text-base font-extrabold text-gray-900">
                    {t_api("Key Facts")}
                  </h3>

                  <div className="mt-4 space-y-3 text-sm text-gray-700">
                    {content?.facts?.map((fact: any, idx: number) => (
                        <div key={idx} className="flex justify-between">
                            <span>{t_api(fact.label, fact.label_ar)}</span>
                            <span className="font-semibold text-gray-900">{fact.value}</span>
                        </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              {/* Divisions cards & Dropdown */}
              <Reveal delayMs={140}>
                <div className="rounded-3xl border border-gray-200 p-6 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                      <Briefcase size={18} className="text-[#F2BB13]" />
                      {t_api("Core Divisions")}
                    </h3>

                    {/* Interactive Dropdown Selector for Companies */}
                    <div className="relative">
                      <select
                        onChange={(e) => {
                          if (e.target.value) navigate(e.target.value);
                        }}
                        className="appearance-none bg-gray-50 border border-gray-200 text-gray-800 text-xs font-semibold rounded-xl px-3 py-2 pr-8 hover:border-[#F2BB13] focus:outline-none focus:ring-2 focus:ring-[#F2BB13]/30 cursor-pointer transition"
                        defaultValue=""
                      >
                        <option value="" disabled>{t_api("Select Division...", "اختر القسم...")}</option>
                        <option value="/hamdan-transport">{t_api("Hamdan Transport Division", "قسم حمدان للنقل")}</option>
                        <option value="/hamdan-mining">{t_api("Hamdan Mining", "حمدان للتعدين")}</option>
                        <option value="/hamdan-construction">{t_api("Hamdan Construction", "حمدان للإنشاءات")}</option>
                        <option value="/hamdan-tyres">{t_api("Hamdan Tires & Battery", "حمدان للإطارات والبطاريات")}</option>
                        <option value="/hamdan-exchange">{t_api("Hamdan Exchange", "حمدان للصرافة")}</option>
                        <option value="/hamdan-plaza">{t_api("Hamdan Plaza Hotel", "فندق حمدان بلازا")}</option>
                        <option value="/al-baz-real-estate">{t_api("Hamdan Real Estates", "حمدان للعقارات")}</option>
                        <option value="/travel-tourism">{t_api("Hamdan Tour & Travel", "حمدان للسياحة والسفر")}</option>
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    {content?.divisions?.filter((d: any) => d.name).map((div: any, idx: number) => {
                      const divName = (div.name || "").toLowerCase();
                      let route = "/about?tab=group";
                      if (divName.includes("transport")) route = "/hamdan-transport";
                      else if (divName.includes("construction")) route = "/hamdan-construction";
                      else if (divName.includes("dunlop") || divName.includes("tires") || divName.includes("battery")) route = "/hamdan-tyres";
                      else if (divName.includes("mining")) route = "/hamdan-mining";
                      else if (divName.includes("exchange")) route = "/hamdan-exchange";
                      else if (divName.includes("plaza") || divName.includes("hotel")) route = "/hamdan-plaza";
                      else if (divName.includes("real") || divName.includes("estate")) route = "/al-baz-real-estate";
                      else if (divName.includes("tour") || divName.includes("travel")) route = "/travel-tourism";

                      return (
                        <Link
                          key={idx}
                          to={route}
                          className="group flex items-center justify-between rounded-2xl border border-gray-200 p-4 hover:border-[#F2BB13] hover:bg-yellow-50/50 transition-all cursor-pointer block"
                        >
                          <div>
                            <p className="font-semibold text-gray-900 group-hover:text-[#C1121F] transition-colors">
                              {t_api(div.name, div.name_ar)}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">{t_api(div.desc, div.desc_ar)}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#F2BB13] shrink-0 ms-2 transition-colors" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </Reveal>

              {/* CTA */}
              <Reveal delayMs={180}>
                <div className="rounded-3xl bg-[#F2BB13] p-6 text-black hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-extrabold">
                    {t_api(content?.contact_cta?.title || "Contact Us", content?.contact_cta?.title_ar)}
                  </h3>
                  <p className="mt-2 text-black/80 text-sm leading-relaxed">
                    {t_api(content?.contact_cta?.desc || "Get in touch with Hamdan Trading Group for any inquiries.", content?.contact_cta?.desc_ar)}
                  </p>
                  <Link to="/contact" className="block mt-4">
                    <button className="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 transition-colors">
                      {t('nav.contact')}
                    </button>
                  </Link>
                </div>
              </Reveal>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default HamdanGroup;
